// Small geo helpers for walk tracking.

export type LatLng = [number, number];

const R = 6_371_000; // Earth radius, metres

/** Great-circle distance in metres. */
export function haversine(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function routeLengthM(route: LatLng[]): number {
  let d = 0;
  for (let i = 1; i < route.length; i++) d += haversine(route[i - 1], route[i]);
  return d;
}

export const KM_PER_MILE = 1.609344;

export function formatMiles(km: number): string {
  const mi = km / KM_PER_MILE;
  return mi < 0.1 ? `${Math.round(km * 1000)} m` : `${mi.toFixed(mi < 10 ? 2 : 1)} mi`;
}

/** Rough human step estimate from distance (0.75 m stride). */
export function stepsFromKm(km: number): number {
  return Math.round((km * 1000) / 0.75);
}

/**
 * Accept a new GPS fix only if it's accurate and represents real movement:
 * ignores poor fixes, jitter while standing still and teleport jumps.
 */
export function acceptFix(prev: LatLng | undefined, next: LatLng, accuracyM: number, secondsSincePrev: number): boolean {
  if (accuracyM > 35) return false;
  if (!prev) return true;
  const d = haversine(prev, next);
  if (d < Math.max(4, accuracyM * 0.5)) return false; // jitter
  const speed = d / Math.max(1, secondsSincePrev);
  return speed < 7; // > ~15 mph is not a puppy walk
}

/** Douglas–Peucker simplification, tolerance in metres (approximate). */
export function simplify(route: LatLng[], toleranceM = 3): LatLng[] {
  if (route.length < 3) return route;
  const tol = toleranceM / 111_000; // degrees, close enough at UK latitudes
  const sqTol = tol * tol;
  const keep = new Uint8Array(route.length);
  keep[0] = keep[route.length - 1] = 1;
  const stack: [number, number][] = [[0, route.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxD = 0;
    let idx = -1;
    for (let i = first + 1; i < last; i++) {
      const d = segDistSq(route[i], route[first], route[last]);
      if (d > maxD) {
        maxD = d;
        idx = i;
      }
    }
    if (idx >= 0 && maxD > sqTol) {
      keep[idx] = 1;
      stack.push([first, idx], [idx, last]);
    }
  }
  return route.filter((_, i) => keep[i]);
}

function segDistSq(p: LatLng, a: LatLng, b: LatLng): number {
  let [x, y] = a;
  let dx = b[0] - x;
  let dy = b[1] - y;
  if (dx !== 0 || dy !== 0) {
    const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      x = b[0];
      y = b[1];
    } else if (t > 0) {
      x += dx * t;
      y += dy * t;
    }
  }
  dx = p[0] - x;
  dy = p[1] - y;
  return dx * dx + dy * dy;
}

export function round5(p: LatLng): LatLng {
  return [Math.round(p[0] * 1e5) / 1e5, Math.round(p[1] * 1e5) / 1e5];
}
